import { fabric } from 'fabric';
import { Group, Object, Textbox } from 'fabric/fabric-impl';
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignStartVertical,
  Crosshair,
  FlipHorizontal,
  FlipVertical,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import alignment from 'ud-ui/components/fabric-canvas/addons/alignment/alignment';
import firstSelection from 'ud-ui/components/fabric-canvas/addons/first-selection';
import history from 'ud-ui/components/fabric-canvas/addons/history';
import { selectFabricManager } from '../../../../store/editor/selectors';
import ChoosingTeam from './components/choosing-team';
import LayerControls from './components/layer-controls';
import LockPosition from './components/lock-position';
import AlignmentButton from './shared/alignment-button';
import { theme } from 'styles/theme/theme';
import { Item, LongDivider, ShortDivider, SubItem } from './styles';

interface IProps {
  objects: Object[];
}

const isHasTeam = (object: Object) => {
  if (object.type !== 'group') return false;
  if (!object.data) return false;
  const lastChar = object.data.name?.slice(-1);
  const lastCharIsNum = /^\d+$/.test(lastChar);
  return object.data.hasTeam || lastCharIsNum || object.data.teamIndex !== undefined;
};

const getPlaceholdersWithTeam = (objects: Object[]): Group[] => {
  return objects.filter(isHasTeam) as Group[];
};

function ManyObjects(props: IProps) {
  const { objects } = props;
  const lockMovementX = objects.some((object) => object.lockMovementX);
  const placeholdersWithTeam = useMemo(() => getPlaceholdersWithTeam(objects), [objects]);
  const fabricManager = useSelector(selectFabricManager);

  const [teamIndex, setTeamIndex] = useState(
    +(placeholdersWithTeam[0]?.data?.name?.at(-1) ?? placeholdersWithTeam[0]?.data?.teamIndex) as 0 | 1,
  );

  const onChangeTeam = useCallback(
    (teamIndex: 0 | 1) => {
      placeholdersWithTeam.forEach((placeholder) => {
        const oldTeamIndex = placeholder.data.teamIndex;

        switch (placeholder.data?.type) {
          case 'composition': {
            if (oldTeamIndex !== undefined) {
              placeholder.data = { ...placeholder.data, teamIndex };
            } else {
              placeholder.data = { ...placeholder.data, team: teamIndex };
            }
            break;
          }
          default: {
            if (oldTeamIndex !== undefined) {
              placeholder.data = { ...placeholder.data, teamIndex };
            } else {
              const name = placeholder.data.name;
              placeholder.data = { ...placeholder.data, name: name.slice(0, -1) + teamIndex };
              const type = placeholder.data.type;
              let text: Textbox | undefined;
              switch (type) {
                case 'text': {
                  text = placeholder.getObjects()[0] as Textbox | undefined;
                  break;
                }
                case 'image': {
                  text = placeholder.getObjects()[1] as Textbox | undefined;
                  break;
                }
              }

              if (!text || !text.text) return;
              const textName = text.text.slice(0, -1) + (teamIndex + 1);
              text?.set({ text: textName });
            }
          }
        }
      });

      setTeamIndex(teamIndex);
      fabricManager?.render();
      history.saveState();
    },
    [fabricManager, placeholdersWithTeam],
  );

  const onCenterFirstToOthersX = useCallback(() => {
    if (objects.length < 2) return;
    const canvas = fabricManager?.canvas;
    if (!canvas) return;
    const firstObj = firstSelection.get() ?? objects[0];
    const otherObjects = objects.filter((o) => o !== firstObj);
    let totalX = 0;
    for (const obj of otherObjects) {
      const bounds = obj.getBoundingRect(true);
      totalX += bounds.left + bounds.width / 2;
    }
    const targetCenterX = totalX / otherObjects.length;
    const firstBounds = firstObj.getBoundingRect(true);
    const targetLeft = targetCenterX - firstBounds.width / 2;
    firstObj.setPositionByOrigin(new fabric.Point(targetLeft, firstObj.top ?? 0), 'left', 'top');
    firstObj.setCoords();
    canvas.requestRenderAll();
    history.saveState();
  }, [objects, fabricManager]);

  const onCenterFirstToOthersY = useCallback(() => {
    if (objects.length < 2) return;
    const canvas = fabricManager?.canvas;
    if (!canvas) return;
    const firstObj = firstSelection.get() ?? objects[0];
    const otherObjects = objects.filter((o) => o !== firstObj);
    let totalY = 0;
    for (const obj of otherObjects) {
      const bounds = obj.getBoundingRect(true);
      totalY += bounds.top + bounds.height / 2;
    }
    const targetCenterY = totalY / otherObjects.length;
    const firstBounds = firstObj.getBoundingRect(true);
    const targetTop = targetCenterY - firstBounds.height / 2;
    firstObj.setPositionByOrigin(new fabric.Point(firstObj.left ?? 0, targetTop), 'left', 'top');
    firstObj.setCoords();
    canvas.requestRenderAll();
    history.saveState();
  }, [objects, fabricManager]);

  const onFlipHorizontal = useCallback(() => {
    const activeObject = fabricManager?.canvas.getActiveObject();
    if (!activeObject) return;
    if (activeObject instanceof fabric.ActiveSelection) {
      activeObject.getObjects().forEach((obj) => {
        obj.set({ flipX: !obj.flipX });
      });
    } else {
      activeObject.set({ flipX: !activeObject.flipX });
    }
    fabricManager?.canvas.requestRenderAll();
    history.saveState();
  }, [fabricManager]);

  const onFlipVertical = useCallback(() => {
    const activeObject = fabricManager?.canvas.getActiveObject();
    if (!activeObject) return;
    if (activeObject instanceof fabric.ActiveSelection) {
      activeObject.getObjects().forEach((obj) => {
        obj.set({ flipY: !obj.flipY });
      });
    } else {
      activeObject.set({ flipY: !activeObject.flipY });
    }
    fabricManager?.canvas.requestRenderAll();
    history.saveState();
  }, [fabricManager]);

  const [isPositionLock, setIsPositionLock] = useState(lockMovementX ?? false);

  useEffect(() => {
    if (!fabricManager) return;
    const activeSelection = fabricManager.canvas.getActiveObject();
    if (!activeSelection) return;
    activeSelection.set({
      lockMovementX: isPositionLock,
      lockMovementY: isPositionLock,
      lockScalingX: isPositionLock,
      lockScalingY: isPositionLock,
      lockUniScaling: isPositionLock,
      lockRotation: isPositionLock,
      lockSkewingX: isPositionLock,
      lockSkewingY: isPositionLock,
      lockScalingFlip: isPositionLock,
    });
  }, [fabricManager, isPositionLock]);

  return (
    <>
      <Item>Группа объектов</Item>
      <ShortDivider />
      <Item>
        <SubItem style={{ width: '100%', justifyContent: 'space-between' }}>
          <AlignmentButton tooltip="По левому краю" onClick={alignment.group.left}>
            <AlignStartVertical size={18} color={theme.colors.EditorAccentBlue} />
          </AlignmentButton>
          <AlignmentButton tooltip="По центру по вертикали" onClick={alignment.group.centerVertical}>
            <AlignCenterVertical size={18} color={theme.colors.EditorAccentBlue} />
          </AlignmentButton>
          <AlignmentButton tooltip="По правому краю" onClick={alignment.group.right}>
            <AlignEndVertical size={18} color={theme.colors.EditorAccentBlue} />
          </AlignmentButton>
          <AlignmentButton tooltip="По верхнему краю" onClick={alignment.group.top}>
            <AlignStartHorizontal size={18} color={theme.colors.EditorAccentBlue} />
          </AlignmentButton>
          <AlignmentButton tooltip="По центру по горизонтали" onClick={alignment.group.centerHorizontal}>
            <AlignCenterHorizontal size={18} color={theme.colors.EditorAccentBlue} />
          </AlignmentButton>
          <AlignmentButton tooltip="По нижнему краю" onClick={alignment.group.bottom}>
            <AlignEndHorizontal size={18} color={theme.colors.EditorAccentBlue} />
          </AlignmentButton>
          <AlignmentButton tooltip="Отразить горизонтально" onClick={onFlipHorizontal}>
            <FlipHorizontal size={18} color={theme.colors.EditorAccentBlue} />
          </AlignmentButton>
          <AlignmentButton tooltip="Отразить вертикально" onClick={onFlipVertical}>
            <FlipVertical size={18} color={theme.colors.EditorAccentBlue} />
          </AlignmentButton>
          <AlignmentButton tooltip="Центровать по X относительно остальных" onClick={onCenterFirstToOthersX}>
            <Crosshair size={18} color={theme.colors.EditorAccentBlue} />
          </AlignmentButton>
          <AlignmentButton tooltip="Центровать по Y относительно остальных" onClick={onCenterFirstToOthersY}>
            <Crosshair size={18} color={theme.colors.EditorAccentOrange} />
          </AlignmentButton>
        </SubItem>
      </Item>
      <LongDivider />
      {placeholdersWithTeam.length > 0 && (
        <Item>
          <ChoosingTeam onChange={onChangeTeam} value={teamIndex} />
        </Item>
      )}
      <LayerControls useActiveObject />
      <LockPosition
        objects={[fabricManager?.canvas.getActiveObject() as Object]}
        onChange={setIsPositionLock}
        value={isPositionLock}
      />
    </>
  );
}

export default ManyObjects;
